import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import PageSection from '../design-system/PageSection';
import PageWrapper from '../design-system/PageWrapper';
import Container from '../design-system/Containers';
import Paragraph from '../design-system/Paragraph';
import RichText, { SecondaryRenderer } from '../design-system/RichText';
import {
  HeadingHero,
  HeadingPara,
  HeadingLabel,
} from '../design-system/Headings';
import MagazineSpread from '../design-system/MagazineSpread';
import { Button } from '../design-system/Buttons';
import { TabCarousel } from '../design-system/TabBar';
import { archetypeContentType } from '../proptypes/archetype-content';
import phrasesType from '../proptypes/phrases';
import ArchetypeSelector from '../components/ArchetypeSelector';
import { Chevron, OpenQuote } from '../components/UiIcons';
import {
  mediaBreakpointUpMD,
  mediaBreakpointUpLG,
} from '../constants/style-constants';
import shopifyConfigType from '../proptypes/shopify-config';
import ShopifyProducts from '../components/ShopifyProducts';
import {
  selectFirstParagraph,
  selectAccreditation,
} from '../selectors/spy-role';
import QualityCard from '../components/QualityCard';
import AccountSubNav from '../components/AccountSubNav';

export const spyRolePagePropTypes = {
  allArchetypeContent: PropTypes.arrayOf(archetypeContentType).isRequired,
  archetypeMatch: PropTypes.string.isRequired,
  phrases: phrasesType.isRequired,
  shopifyConfig: shopifyConfigType.isRequired,
  shopifyCollection: PropTypes.string.isRequired,
  showSpyRoleSubHeading: PropTypes.bool,
};

const SPONSOR_CONTENT_KEY = 'Argylle';
const SPONSOR_URL = 'https://www.argyllemovie.com/';
const SPONSOR_IMAGE_ALT = 'Argylle';
const SPONSOR_LOGO_IMG = 'https://images.ctfassets.net/oblvw8t6qqwi/21eHcmKtnHQky0QTBiXRft/7dcfe5728caa8a7427a6b30c5a3bb473/387453K5-ARG_TT_sRGB.png?h=75';
const SPONSOR_BANNER_IMG = 'https://images.ctfassets.net/oblvw8t6qqwi/1hyx0l0I3UCBfMVSbKu3Z4/29a3dec1f0c0b49b7c2edb01c567abb8/03_ARG_APPLE_2048x428_MLR_F02_120823.jpg?w=1000&q=80';

// Source at contentful; hover over download button to get download link.
// Remember to change the legacy spy profile at spy_profiles/show.html.erb as well.
// Link below is TBC
const SponsorLogo = () => (
  <a href={SPONSOR_URL}>
    <img
      alt={SPONSOR_IMAGE_ALT}
      src={SPONSOR_LOGO_IMG}
      style={{ width: '100%' }}
      className="mb-5"
    />
  </a>
);

const SponsorBanner = () => (
  <a href={SPONSOR_URL}>
    <img
      alt={SPONSOR_IMAGE_ALT}
      src={SPONSOR_BANNER_IMG}
      style={{ width: '100%' }}
    />
  </a>
);

const SpyRolePage = ({
  archetypeMatch,
  phrases,
  allArchetypeContent,
  shopifyConfig,
  shopifyCollection,
  showSpyRoleSubHeading,
}) => {
  const [currentArchetypeId, setCurrentArchetypeId] = useState(archetypeMatch);
  const yourArchetype = allArchetypeContent.find(
    ({ id }) => id === archetypeMatch,
  );
  const currentArchetype = allArchetypeContent.find(
    ({ id }) => id === currentArchetypeId,
  );
  const roleDescription = selectFirstParagraph(yourArchetype.yourRole);
  const { quoteName, quoteJobTitle } = selectAccreditation(
    currentArchetype.insiderViewQuoteAttribution,
  );

  const handleChangeRoleClick = (id) => {
    setCurrentArchetypeId(id);
  };

  const handleChevronClick = (offset) => {
    const index = allArchetypeContent.findIndex(
      ({ id }) => id === currentArchetypeId,
    );
    const isAboveRange = index + offset >= allArchetypeContent.length;
    const isBelowRange = index + offset <= -1;
    const nextIndex = isAboveRange || isBelowRange ? index : index + offset;
    const nextId = allArchetypeContent[nextIndex].id;
    setCurrentArchetypeId(nextId);
  };

  const getRandomElement = arr => arr[Math.floor(Math.random() * arr.length)];

  const selectSponsorCharacterMatches = () => {
    if (!SPONSOR_CONTENT_KEY) return [];

    const possibleSponsorCharacters = (currentArchetype.characters || [])
      .filter(c => c.type === SPONSOR_CONTENT_KEY)
      .map(
        ({ type, ...rest }) => ({
          key: type,
          ...rest,
        }),
      );
    if (possibleSponsorCharacters.length === 0) return [];
    const result = [
      { letterbox: true, ...getRandomElement(possibleSponsorCharacters) },
    ];
    return result;
  };

  const sections = [
    {
      key: 'role',
      image: currentArchetype.aboutRoleImage,
      imageAlt: phrases.spyRole.about.theRoleTitle,
      heading: phrases.spyRole.about.theRoleTitle,
      description: currentArchetype.yourRole,
    },
    ...selectSponsorCharacterMatches(),
    {
      key: 'realLife',
      image: currentArchetype.realLifePortraitImage,
      imageAlt: phrases.spyRole.about.realRoleModelTitle,
      heading: phrases.spyRole.about.realRoleModelTitle,
      description: currentArchetype.roleModel,
    },
    {
      key: 'fictional',
      image: currentArchetype.fictionalPortraitImage,
      imageAlt: phrases.spyRole.about.fictionalRoleModelTitle,
      heading: phrases.spyRole.about.fictionalRoleModelTitle,
      description: currentArchetype.leadingRole,
    },
    {
      key: 'operational',
      image: currentArchetype.operationImage,
      imageAlt: phrases.spyRole.about.typicalOperationTitle,
      heading: phrases.spyRole.about.typicalOperationTitle,
      description: currentArchetype.typicalOperation,
    },
  ].filter(section => section !== undefined);

  return (
    <PageWrapper tallNav bgDark>
      <AccountSubNav />
      <PageSection
        hero
        center
        className="spy-role__heading"
        borderB
        css={css`
          background-image: url(${yourArchetype.roleWallpaperImage}?q=60&w=3000);
`}
      >
        {showSpyRoleSubHeading && (
          <HeadingLabel bright>{`${phrases.spyRole.heading}`}</HeadingLabel>
        )}
        <HeadingHero my0>{yourArchetype.name}</HeadingHero>
        <div className="row justify-content-center">
          <Paragraph sub className="col-10 col-lg-8">
            {roleDescription}
          </Paragraph>
        </div>
      </PageSection>
      <PageSection className="spy-role__key_qualities" center>
        <HeadingPara>{phrases.spyRole.qualitiesTitle}</HeadingPara>
        <Paragraph>{phrases.spyRole.qualitiesBody}</Paragraph>
        <div className="row">
          {yourArchetype.keyAttributes.map(QualityCard)}
        </div>
      </PageSection>
      <PageSection
        className="spy-role__explore_roles"
        container={false}
        center
        bgGradUp
        pyLg
        borderT
      >
        <Container>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <Button
              frameless
              className="ss_sm-hide"
              onClick={() => handleChevronClick(-1)}
            >
              <Chevron rotate={180} />
            </Button>
            <HeadingPara pyMd className="w-100">
              {phrases.spyRole.exploreRolesTitle}
            </HeadingPara>
            <Button
              frameless
              className="ss_sm-hide"
              onClick={() => handleChevronClick(1)}
            >
              <Chevron />
            </Button>
          </div>
        </Container>
        <TabCarousel
          selectedId={currentArchetypeId}
          tabContent={allArchetypeContent}
          onTabClick={handleChangeRoleClick}
        />
      </PageSection>
      <PageSection
        center
        bgDarkMid
        css={css`
          background-image: url(${currentArchetype.expertQuoteImage}?q=60&w=3000);
`}
      >
        <OpenQuote
          css={css`
            height: 50px;
            ${mediaBreakpointUpMD} {
              height: 75px;
            }
            ${mediaBreakpointUpLG} {
              height: 100px;
            } ;
`}
        />
        <ReactMarkdown
          unwrapDisallowed
          disallowedTypes={['emphasis']}
          renderers={{ paragraph: HeadingPara }}
        >
          {currentArchetype.insiderViewQuote}
        </ReactMarkdown>
        <div className="row justify-content-center">
          <div className="col-10 col-lg-6">
            <Paragraph strongSub my0>
              {quoteName}
            </Paragraph>
            <Paragraph sub my0>
              {quoteJobTitle}
            </Paragraph>
          </div>
        </div>
      </PageSection>
      <PageSection bgGradDown>
        {sections.map(
          ({
            key, image, imageAlt, heading, description, letterbox,
          }, i) => {
            const MagazineContent = (
              <MagazineSpread
                imageAlt={imageAlt}
                image={`${image}?q=60`}
                imageFirst={i % 2 === 0}
                key={key}
                letterbox={letterbox}
              >
                {key === SPONSOR_CONTENT_KEY && <SponsorLogo />}
                {key === SPONSOR_CONTENT_KEY && (
                <Paragraph sub secondary my0>
                  {phrases.spyRole.about.characterMatch}
                </Paragraph>
                )}
                <div style={{
                  color: 'rgb(169, 179, 207)',
                  textTransform: 'uppercase',
                  fontStyle: 'italic',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                }}
                >
                  {heading}
                </div>
                <RichText
                  paragraphRenderer={SecondaryRenderer}
                  content={description}
                />
                {key === SPONSOR_CONTENT_KEY && <SponsorBanner />}
              </MagazineSpread>
            );

            return key === (SPONSOR_CONTENT_KEY)
              ? <a href={SPONSOR_URL}>{MagazineContent}</a>
              : MagazineContent;
          },
        )}
      </PageSection>
      <PageSection center>
        <ArchetypeSelector
          archetypes={allArchetypeContent}
          onClick={handleChangeRoleClick}
          phrases={phrases.spyRole}
          selected={currentArchetypeId}
        />
      </PageSection>
      <PageSection borderT className="shopify-products-section" center>
        <HeadingPara>{phrases.shopify.sectionTitle}</HeadingPara>
        <ShopifyProducts
          config={shopifyConfig}
          collectionId={shopifyCollection}
          count={6}
          linkText={phrases.shopify.purchaseButtonTitle}
        />
      </PageSection>
    </PageWrapper>
  );
};

export default SpyRolePage;

SpyRolePage.defaultProps = {
  showSpyRoleSubHeading: false,
};

SpyRolePage.propTypes = spyRolePagePropTypes;
